import {DrawFrmThing} from "../../../drawfrm/DrawFrmThing";
import {inches, lineBlueHeavy} from "../../../const";
import {FrmShape} from "../../../drawfrm/FrmShape";
import {FrameProfileArgs} from "./ProfileArgs";
import {FrmRefPoint} from "../../../drawfrm/FrmRefPoint";

// Thermal-break (TB) — a two-piece (telescoping) frame, like EXP, but with
// plain butted returns (just a vertical return, no horizontal return lip). The
// head piece and the wall piece interlock with a small air gap between them.
// Geometry mirrors FrameProfileTB.svg (drawn at ~40.7px/inch).
//
// As with EXP, the pieces overlap vertically, so the outline can't be traced as
// a single monotonic top-to-bottom path. We trace it as one chain with a single
// non-drawn "pen-up" bridge across the gap (see FrmShapeLine.penUp): the pieces
// stay linked in the point chain (and move together) but render as two segments.
//
// Note: because the gap connector is diagonal, a jamb-depth dimension spanning
// the whole profile would not equal the sum of its vertical lines and would
// corrupt the shared jambDepth var. So only the (responsive) jamb face is
// dimensioned here; the depth is shown at the drawn proportions.
export class FrameProfileTB extends DrawFrmThing {

    primaryDoorAnchor = new FrmRefPoint();
    // top of the outer face; the frame-profile view aligns a clamshell mullion on this.
    faceAnchor = new FrmRefPoint();

    constructor(args: FrameProfileArgs) {
        super();
        const {detail, jambDepth, jambFace, view, app, left, swingIn, clamshell, hideFaces, hideFaceDims, flipY} = args;

        const shape = new FrmShape(`frameProfile:TB:${left ? "left" : "right"}`, {
            dimensionMode: view.dimensionMode,
            thing: this,
            app: app,
        });
        shape.strokeColor = lineBlueHeavy;
        shape.horizontalDimensionSide = left ? "left" : "right";
        if (!left) shape.invertX();
        if((!swingIn) !== !!flipY) shape.invertY();

        //   __________            head piece: top return + top face
        //  |          |
        //  |       ___|           steps out to the stop
        //  |      |  |            interlock lip up into the gap
        //  |      :              <- gap (pieces interlock here)
        //  |   ___|               wall piece: lip + lower throat
        //  |  |
        //  |__|________           bottom face + bottom return

        const returnV = 0.638 * inches;      // top/bottom return, vertical
        const face = 2 * inches;             // jamb face width
        const upperThroat = 1.492 * inches;  // throat from the face down to the stop
        const stopOut = 0.900 * inches;      // step out to the door stop
        const upperStop = 2.636 * inches;    // upper stop face
        // const upperLipBack = 0.728 * inches; // lip back underneath the stop
        const interlockUp = 0.443 * inches;  // interlock lip up into the gap

        // gap bridge (not drawn)
        const gapX = 1/4 * inches;         // horizontal offset across the gap
        const gapY = gapX;         // vertical offset across the gap

        // wall piece (lower)
        const lowerThroat = 2.174 * inches;  // throat down to the bottom face

        // --- head piece: top return then top face ---
        shape.start(0, 0);
        const topReturn = shape.lineTo(0, -returnV);
        if(clamshell) topReturn.setPenUp();
        const topFace = shape.lineTo(face, 0);

        // upper throat then step out to the stop, down the stop face, back under
        // it, then up the interlock lip into the gap
        const upperThroatLine = shape.lineTo(0, upperThroat).linkDimension(detail.rabbetDepth);
        shape.lineTo(stopOut, 0);
        shape.lineTo(0, upperStop);
        const lipBack = stopOut - gapX;
        shape.lineTo(-lipBack, 0);
        shape.lineTo(0, -interlockUp);

        // the door closes against the stop; anchor at the bottom of the upper
        // throat (door extends up from here)
        this.primaryDoorAnchor.setLocalPoint(upperThroatLine.pointB);

        // --- gap: bridge across to the wall piece without drawing it ---
        const gapLine = shape.lineTo(lipBack-gapX, -gapY);
        gapLine.setPenUp();

        // --- wall piece: lip, throat, bottom face, bottom return ---
        shape.lineTo(-lipBack, 0);
        shape.lineTo(0, lowerThroat).linkDimension(detail.bottomRabbetDepth);
        const bottomFace = shape.lineTo(-face, 0);
        const bottomReturn = shape.lineTo(0, -returnV);
        if(clamshell) bottomReturn.setPenUp();

        topReturn.dimMatches(bottomReturn);

        topFace.linkDimension(jambFace);
        bottomFace.dimMatches(topFace);

        shape.addDimension(topFace.pointA, bottomFace.pointB, jambDepth);
        // clamshell secondary side: omit the top/bottom faces so the primary side's
        // faces cap the merged mullion (avoids a doubled/gapped cap).
        if(hideFaces) { topFace.setPenUp(); bottomFace.setPenUp(); }
        if(hideFaces || hideFaceDims) shape.hideDimensionsForVars([jambFace, jambDepth]);
        this.faceAnchor.setLocalPoint(topFace.getStartpoint());
        shape.done();
    }
}
